import { useCallback, useEffect, useReducer } from "react";
import type { PSDTemplate } from "../../../../../models/MockupConfig/index";
import {
  canvasPixelsToInches,
  formatInches,
  formatLogoSizeLabel,
} from "../../../../../utils/logoSizeUnits";
import type { useLogoPlacement } from "./useLogoPlacement";

type LogoPlacementApi = ReturnType<typeof useLogoPlacement>;

type SizeFocus = "width" | "height" | null;

type LogoMetadataState = {
  rotationDraft: string;
  rotationFocused: boolean;
  widthDraft: string;
  heightDraft: string;
  sizeFocused: SizeFocus;
  sizeManuallyEdited: boolean;
  rgbCodeDraft: string;
  cmykCodeDraft: string;
};

type LogoMetadataAction =
  | { type: "SET_ROTATION_DRAFT"; value: string }
  | { type: "SET_ROTATION_FOCUSED"; value: boolean }
  | { type: "SYNC_ROTATION"; value: string }
  | { type: "SET_WIDTH_DRAFT"; value: string }
  | { type: "SET_HEIGHT_DRAFT"; value: string }
  | { type: "SET_SIZE_FOCUSED"; value: SizeFocus }
  | { type: "SET_SIZE_MANUAL"; value: boolean }
  | { type: "SYNC_SIZE"; width: string; height: string }
  | { type: "COMMIT_SIZE"; dimension: "width" | "height"; value: string }
  | { type: "SET_RGB"; value: string }
  | { type: "SET_CMYK"; value: string }
  | { type: "RESET" };

const INITIAL_STATE: LogoMetadataState = {
  rotationDraft: "0",
  rotationFocused: false,
  widthDraft: "",
  heightDraft: "",
  sizeFocused: null,
  sizeManuallyEdited: false,
  rgbCodeDraft: "",
  cmykCodeDraft: "",
};

function logoMetadataReducer(
  state: LogoMetadataState,
  action: LogoMetadataAction,
): LogoMetadataState {
  switch (action.type) {
    case "SET_ROTATION_DRAFT":
      return { ...state, rotationDraft: action.value };
    case "SET_ROTATION_FOCUSED":
      return { ...state, rotationFocused: action.value };
    case "SYNC_ROTATION":
      return { ...state, rotationDraft: action.value };
    case "SET_WIDTH_DRAFT":
      return { ...state, widthDraft: action.value, sizeManuallyEdited: true };
    case "SET_HEIGHT_DRAFT":
      return { ...state, heightDraft: action.value, sizeManuallyEdited: true };
    case "SET_SIZE_FOCUSED":
      return { ...state, sizeFocused: action.value };
    case "SET_SIZE_MANUAL":
      return { ...state, sizeManuallyEdited: action.value };
    case "SYNC_SIZE":
      return { ...state, widthDraft: action.width, heightDraft: action.height };
    case "COMMIT_SIZE":
      return {
        ...state,
        sizeFocused: null,
        sizeManuallyEdited: true,
        widthDraft: action.dimension === "width" ? action.value : state.widthDraft,
        heightDraft: action.dimension === "height" ? action.value : state.heightDraft,
      };
    case "SET_RGB":
      return { ...state, rgbCodeDraft: action.value };
    case "SET_CMYK":
      return { ...state, cmykCodeDraft: action.value };
    case "RESET":
      return { ...INITIAL_STATE };
    default:
      return state;
  }
}

/**
 * Informational drafts for rotation input + print metadata (size/RGB/CMYK).
 * Size/color drafts do not resize or recolor the logo on canvas.
 */
export function useLogoMetadataDrafts({
  open,
  logoPlacement,
  selectedPsdTemplate,
  selectedPsdId,
}: {
  open: boolean;
  logoPlacement: LogoPlacementApi;
  selectedPsdTemplate: PSDTemplate | undefined;
  selectedPsdId: number | null;
}) {
  const [state, dispatch] = useReducer(logoMetadataReducer, INITIAL_STATE);

  useEffect(() => {
    if (!open) dispatch({ type: "RESET" });
  }, [open]);

  useEffect(() => {
    if (state.rotationFocused) return;
    dispatch({
      type: "SYNC_ROTATION",
      value: String(Math.round(logoPlacement.logoTransform.rotation)),
    });
  }, [logoPlacement.logoTransform.rotation, state.rotationFocused]);

  const syncSizeDraftsFromCanvas = useCallback(() => {
    const canvasW = selectedPsdTemplate?.canvas_w ?? 800;
    const canvasH = selectedPsdTemplate?.canvas_h ?? 600;
    const coords = logoPlacement.getCanvasCoords(canvasW, canvasH);
    if (!coords) return;
    dispatch({
      type: "SYNC_SIZE",
      width: formatInches(canvasPixelsToInches(coords.width)),
      height: formatInches(canvasPixelsToInches(coords.height)),
    });
  }, [logoPlacement, selectedPsdTemplate]);

  useEffect(() => {
    if (state.sizeFocused || state.sizeManuallyEdited) return;
    if (!logoPlacement.logoUrl || !selectedPsdTemplate) return;
    syncSizeDraftsFromCanvas();
  }, [
    logoPlacement.logoTransform.width,
    logoPlacement.logoTransform.height,
    logoPlacement.logoUrl,
    selectedPsdTemplate,
    state.sizeFocused,
    state.sizeManuallyEdited,
    syncSizeDraftsFromCanvas,
  ]);

  useEffect(() => {
    dispatch({ type: "SET_SIZE_MANUAL", value: false });
  }, [logoPlacement.logoUrl, selectedPsdId]);

  const commitRotationDraft = useCallback(() => {
    const raw = state.rotationDraft.trim();
    if (!raw) {
      dispatch({
        type: "SYNC_ROTATION",
        value: String(Math.round(logoPlacement.logoTransform.rotation)),
      });
      dispatch({ type: "SET_ROTATION_FOCUSED", value: false });
      return;
    }
    const value = Number(raw);
    if (Number.isFinite(value)) {
      logoPlacement.setRotation(value);
    } else {
      dispatch({
        type: "SYNC_ROTATION",
        value: String(Math.round(logoPlacement.logoTransform.rotation)),
      });
    }
    dispatch({ type: "SET_ROTATION_FOCUSED", value: false });
  }, [logoPlacement, state.rotationDraft]);

  const commitCapturedSizeDraft = useCallback(
    (dimension: "width" | "height") => {
      const raw = (dimension === "width" ? state.widthDraft : state.heightDraft).trim();
      if (!raw) {
        if (!state.sizeManuallyEdited) syncSizeDraftsFromCanvas();
        dispatch({ type: "SET_SIZE_FOCUSED", value: null });
        return;
      }
      const inches = Number(raw);
      if (!Number.isFinite(inches) || inches <= 0) {
        if (!state.sizeManuallyEdited) syncSizeDraftsFromCanvas();
        dispatch({ type: "SET_SIZE_FOCUSED", value: null });
        return;
      }
      dispatch({ type: "COMMIT_SIZE", dimension, value: formatInches(inches) });
    },
    [state.widthDraft, state.heightDraft, state.sizeManuallyEdited, syncSizeDraftsFromCanvas],
  );

  const getCapturedLogoSizeLabel = useCallback((): string | undefined => {
    const widthInches = Number(state.widthDraft.trim());
    const heightInches = Number(state.heightDraft.trim());
    if (
      !Number.isFinite(widthInches) ||
      !Number.isFinite(heightInches) ||
      widthInches <= 0 ||
      heightInches <= 0
    ) {
      return undefined;
    }
    return formatLogoSizeLabel(widthInches, heightInches);
  }, [state.widthDraft, state.heightDraft]);

  const getCapturedColorCode = useCallback((value: string): string | undefined => {
    const trimmed = value.trim();
    return trimmed || undefined;
  }, []);

  return {
    rotationDraft: state.rotationDraft,
    setRotationDraft: (value: string) =>
      dispatch({ type: "SET_ROTATION_DRAFT", value }),
    setRotationFocused: (value: boolean) =>
      dispatch({ type: "SET_ROTATION_FOCUSED", value }),
    commitRotationDraft,
    widthDraft: state.widthDraft,
    heightDraft: state.heightDraft,
    setWidthDraft: (value: string) => dispatch({ type: "SET_WIDTH_DRAFT", value }),
    setHeightDraft: (value: string) => dispatch({ type: "SET_HEIGHT_DRAFT", value }),
    setSizeFocused: (value: SizeFocus) =>
      dispatch({ type: "SET_SIZE_FOCUSED", value }),
    commitCapturedSizeDraft,
    rgbCodeDraft: state.rgbCodeDraft,
    setRgbCodeDraft: (value: string) => dispatch({ type: "SET_RGB", value }),
    cmykCodeDraft: state.cmykCodeDraft,
    setCmykCodeDraft: (value: string) => dispatch({ type: "SET_CMYK", value }),
    getCapturedLogoSizeLabel,
    getCapturedColorCode,
  };
}
